import './_bootstrap';

(function (Drupal, once, drupalSettings) {
  const voteStateByNid = new Map();

  const normalizeTitle = (text) => (text || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const parseCount = (text) => {
    const source = text || '';
    const match = source.match(/-?\d+(?:\.\d+)?/);
    return match ? Math.max(0, Math.round(parseFloat(match[0]))) : 0;
  };

  const getPollResultsHost = () => document.querySelector('#block-book-club-book-club-poll-results');

  const getPollResultItems = () => {
    const host = getPollResultsHost();
    if (!host) {
      return [];
    }

    return Array.from(host.querySelectorAll('.book-club-poll-results__item'));
  };

  const getStateEndpoint = () => `${drupalSettings.path.baseUrl}book-club/poll-state/ajax`;

  const getRowNid = (row) => {
    if (!row) {
      return null;
    }

    const formId = row.querySelector('input[name="form_id"]')?.value || '';
    const formIdMatch = formId.match(/node_book_suggestion_(\d+)_/);
    if (formIdMatch) {
      return parseInt(formIdMatch[1], 10);
    }

    const formClass = row.querySelector('form')?.className || '';
    const classMatch = formClass.match(/node-book-suggestion-(\d+)-/);
    if (classMatch) {
      return parseInt(classMatch[1], 10);
    }

    return null;
  };

  const getRowTitle = (row) => row.querySelector('.bc-poll-card__title')?.textContent || '';

  const ensureVoteShell = (row) => {
    const votes = row.querySelector('.bc-poll-card__votes');
    if (!votes) {
      return null;
    }

    let shell = votes.querySelector('.bc-vote-pill');
    if (!shell) {
      shell = document.createElement('button');
      shell.type = 'button';
      shell.className = 'bc-vote-pill';
      shell.innerHTML = '<span class="bc-vote-pill-icon" aria-hidden="true"></span><span class="bc-vote-pill-count"></span>';
      votes.prepend(shell);
    }

    return shell;
  };

  const setVoteResultText = (row, count) => {
    const shell = ensureVoteShell(row);
    if (!shell) {
      return;
    }

    const voteCount = shell.querySelector('.bc-vote-pill-count');
    voteCount.textContent = count === 1 ? '1 vote' : `${count} votes`;
    voteCount.setAttribute('aria-live', 'polite');
  };

  const buildSidebarCountMap = () => {
    const map = new Map();
    getPollResultItems().forEach((item) => {
      const link = item.querySelector('.book-club-poll-results__link');
      const badge = item.querySelector('.book-club-poll-results__count');
      if (!link || !badge) {
        return;
      }

      const key = normalizeTitle(link.textContent || '');
      if (!key) {
        return;
      }

      map.set(key, parseCount(badge.textContent || ''));
    });

    return map;
  };

  const refreshSidebarPercentages = () => {
    const items = getPollResultItems();
    if (!items.length) {
      return;
    }

    // Percentages are recalculated client-side after live vote updates so the
    // sidebar remains accurate without a full page refresh.
    const totalVotes = items.reduce((total, item) => (
      total + parseCount(item.dataset.bookPollCount || item.querySelector('.book-club-poll-results__count')?.textContent || '0')
    ), 0);

    items.forEach((item) => {
      const count = parseCount(item.dataset.bookPollCount || item.querySelector('.book-club-poll-results__count')?.textContent || '0');
      const percentage = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
      const percentageLabel = item.querySelector('.book-club-poll-results__percent');
      const fill = item.querySelector('.book-club-poll-results__bar-fill');

      item.dataset.bookPollPercentage = `${percentage}`;

      if (percentageLabel) {
        percentageLabel.textContent = `${percentage}%`;
      }

      if (fill) {
        fill.style.setProperty('--book-poll-percentage', `${percentage}%`);
      }
    });
  };

  const syncSidebarBadgeForTitle = (titleRaw, count) => {
    const title = normalizeTitle(titleRaw);
    if (!title || Number.isNaN(count)) {
      return;
    }

    const items = getPollResultItems();
    if (!items.length) {
      return;
    }

    items.forEach((item) => {
      const link = item.querySelector('.book-club-poll-results__link');
      const badge = item.querySelector('.book-club-poll-results__count');
      if (!link || !badge) {
        return;
      }

      if (normalizeTitle(link.textContent || '') === title) {
        item.dataset.bookPollCount = `${count}`;
        badge.textContent = count === 1 ? '1 vote' : `${count} votes`;
      }
    });

    refreshSidebarPercentages();
  };

  const reorderSidebarFromState = (state) => {
    if (!state || typeof state !== 'object') {
      return;
    }

    const host = getPollResultsHost();
    const list = host?.querySelector('.book-club-poll-results__list');
    if (!list) {
      return;
    }

    const items = Array.from(list.children)
      .map((wrapper) => ({
        wrapper,
        item: wrapper.matches('.book-club-poll-results__item')
          ? wrapper
          : wrapper.querySelector('.book-club-poll-results__item'),
      }))
      .filter(({ item }) => !!item);
    if (!items.length) {
      return;
    }

    const getSortMeta = ({ wrapper, item }, index) => {
      const nid = parseInt(item.dataset.bookPollNid || '', 10);
      const fallbackTitle = normalizeTitle(item.dataset.bookPollTitle || item.textContent || '');
      const entry = Number.isNaN(nid) ? null : state[nid];
      const count = entry ? parseCount(`${entry.count}`) : parseCount(item.dataset.bookPollCount || '');
      const created = entry?.created || 0;
      const title = normalizeTitle(entry?.title || fallbackTitle);

      return {
        wrapper,
        item,
        index,
        count,
        created,
        title,
      };
    };

    const sorted = items
      .map((item, index) => getSortMeta(item, index))
      .sort((a, b) => {
        if (b.count !== a.count) {
          return b.count - a.count;
        }
        if (b.created !== a.created) {
          return b.created - a.created;
        }
        if (a.title !== b.title) {
          return a.title.localeCompare(b.title);
        }
        return a.index - b.index;
      });

    sorted.forEach(({ wrapper, item, count, title }) => {
      item.dataset.bookPollCount = `${count}`;
      item.dataset.bookPollTitle = title;
      list.appendChild(wrapper);
    });

    refreshSidebarPercentages();
  };

  const normalizeVotePills = (context = document) => {
    if (!context.querySelectorAll) {
      return;
    }

    context.querySelectorAll('.view-id-book_poll .views-row').forEach((row) => {
      const votes = row.querySelector('.bc-poll-card__votes');
      if (!votes) {
        return;
      }

      ensureVoteShell(row);
      row.querySelectorAll('.bc-poll-card__votes .rating-table tr td:not(.rating-table-td)').forEach((td) => {
        td.setAttribute('hidden', 'hidden');
        td.style.display = 'none';
        td.style.visibility = 'hidden';
        td.style.width = '0';
        td.style.maxWidth = '0';
        td.style.overflow = 'hidden';
      });
      row.querySelectorAll('.bc-poll-card__votes .rate-description, .bc-poll-card__votes .messages, .bc-poll-card__votes .status-message')
        .forEach((el) => el.remove());

      const customCount = votes.querySelector('.bc-vote-pill-count');
      const voteResult = row.querySelector('.bc-poll-card__votes .vote-result');
      const count = parseCount(customCount?.textContent || voteResult?.textContent || '0');
      setVoteResultText(row, count);
    });
  };

  const syncCardCountsFromSidebar = (context = document) => {
    if (!context.querySelectorAll) {
      return;
    }

    const countMap = buildSidebarCountMap();
    if (!countMap.size) {
      return;
    }

    context.querySelectorAll('.view-id-book_poll .views-row').forEach((row) => {
      const title = normalizeTitle(getRowTitle(row));
      if (!title || !countMap.has(title)) {
        return;
      }

      setVoteResultText(row, countMap.get(title));
    });
  };

  const fetchPollState = () => fetch(getStateEndpoint(), {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      Accept: 'application/json',
    },
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((payload) => payload?.state || null)
    .catch(() => null);

  const applyPollState = (state, context = document) => {
    if (!state || typeof state !== 'object' || !context.querySelectorAll) {
      return;
    }

    context.querySelectorAll('.view-id-book_poll .views-row').forEach((row) => {
      const nid = getRowNid(row);
      if (!nid || !state[nid]) {
        return;
      }

      const entry = state[nid];
      voteStateByNid.set(nid, !!entry.hasVote);
      setVoteResultText(row, entry.count);
      syncSidebarBadgeForTitle(entry.title || getRowTitle(row), entry.count);
    });

    reorderSidebarFromState(state);
  };

  const refreshFromPollState = (context = document) => fetchPollState().then((state) => {
    if (state) {
      applyPollState(state, context);
      normalizeVotePills(context);
    }
  });

  let refreshTimer = null;
  const queuePollStateRefresh = (delay = 0) => {
    if (refreshTimer) {
      window.clearTimeout(refreshTimer);
    }

    refreshTimer = window.setTimeout(() => {
      refreshFromPollState(document).finally(() => {
        refreshTimer = null;
      });
    }, delay);
  };

  const optimisticVoteUpdate = (radio) => {
    const row = radio.closest('.views-row');
    if (!row) {
      return;
    }

    const nid = getRowNid(row);
    if (!nid) {
      return;
    }

    const voteResult = row.querySelector('.bc-poll-card__votes .vote-result');
    const customCount = row.querySelector('.bc-poll-card__votes .bc-vote-pill-count');
    const currentCount = parseCount(customCount?.textContent || voteResult?.textContent || '0');
    const knownHasVote = voteStateByNid.has(nid) ? voteStateByNid.get(nid) : !!radio.checked;
    const nextHasVote = !knownHasVote;

    // Avoid transient undercount flashes: only optimistic-update when adding a vote.
    // Decrement/unvote paths rely on server reconciliation for accurate counts.
    if (!nextHasVote) {
      return { optimistic: false };
    }

    voteStateByNid.set(nid, true);

    const nextCount = Math.max(0, currentCount + 1);
    setVoteResultText(row, nextCount);
    syncSidebarBadgeForTitle(getRowTitle(row), nextCount);
    return { optimistic: true };
  };

  const removeTransientVoteMessages = (context = document) => {
    if (!context.querySelectorAll) {
      return;
    }

    context.querySelectorAll('.view-id-book_poll .messages, .view-id-book_poll .status-message, .view-id-book_poll .rate-description')
      .forEach((el) => el.remove());
  };

  const installVotesMutationObserver = (context = document) => {
    once('book-poll-votes-mutation', '.view-id-book_poll .bc-poll-card__votes', context).forEach((container) => {
      let scheduled = false;
      const observer = new MutationObserver(() => {
        if (scheduled) {
          return;
        }
        scheduled = true;
        window.requestAnimationFrame(() => {
          scheduled = false;
          const row = container.closest('.views-row');
          if (row) {
            normalizeVotePills(row);
            removeTransientVoteMessages(row);
          }
        });
      });
      observer.observe(container, { childList: true, subtree: true, characterData: true });
    });
  };

  const installVoteAjaxObserver = (context = document) => {
    if (!window.jQuery) {
      return;
    }

    once('book-poll-rate-ajax-refresh', 'body', context).forEach(() => {
      window.jQuery(document).ajaxSuccess((_event, _xhr, settings) => {
        const url = settings?.url || '';
        if (typeof url === 'string' && url.includes('/rate/')) {
          normalizeVotePills(document);
          removeTransientVoteMessages(document);
          queuePollStateRefresh(0);
          queuePollStateRefresh(150);
        }
      });
    });
  };

  Drupal.behaviors.bookClubTheme = {
    attach(context) {
      normalizeVotePills(context);
      removeTransientVoteMessages(context);

      once('book-poll-pill-click', '.view-id-book_poll .views-row', context).forEach((row) => {
        const shell = ensureVoteShell(row);
        if (!shell) {
          return;
        }

        const triggerVote = () => {
          const radio = row.querySelector('input.thumbsup-rating-input[type="radio"]');
          if (!radio) {
            return;
          }
          radio.click();
        };

        shell.addEventListener('click', triggerVote);
        shell.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            triggerVote();
          }
        });
      });

      // Auto-submit votes with Rate's existing AJAX submit element.
      once('book-poll-autovote', '.view-id-book_poll input.thumbsup-rating-input[type="radio"]', context).forEach((radio) => {
        const submitVote = () => {
          if (radio.dataset.submitting === '1') {
            return;
          }

          const form = radio.closest('form');
          if (!form) {
            return;
          }

          const submit = form.querySelector('input.thumbsup-rating-submit, button.thumbsup-rating-submit');
          if (!submit) {
            return;
          }

          radio.dataset.submitting = '1';
          const optimistic = optimisticVoteUpdate(radio);
          normalizeVotePills(document);
          removeTransientVoteMessages(document);
          submit.click();

          if (optimistic?.optimistic) {
            // Increment path can safely reconcile immediately for snappy UX.
            queuePollStateRefresh(0);
            queuePollStateRefresh(220);
          }
          else {
            // Decrement/no-op path skips the immediate fetch to avoid transient flicker.
            queuePollStateRefresh(220);
            queuePollStateRefresh(420);
          }

          window.setTimeout(() => {
            radio.dataset.submitting = '0';
            normalizeVotePills(document);
            removeTransientVoteMessages(document);
          }, 550);
        };

        radio.addEventListener('change', submitVote);
        radio.addEventListener('click', () => {
          window.setTimeout(submitVote, 0);
        });
      });

      installVoteAjaxObserver(context);
      installVotesMutationObserver(context);

      // Initial sync on page load.
      syncCardCountsFromSidebar(context);
      queuePollStateRefresh(0);
      queuePollStateRefresh(120);
    },
  };
})(Drupal, once, drupalSettings);
